import { pick, concat, findIndex, set, merge } from 'lodash'
import { http, notify } from 'utils'
import { checkEmail } from 'utils/errorHelpers'

// Request mutation imports.
import {
  SET_REQUEST, SET_RECOMMENDATIONS, SET_RECOMMENDATIONS_PAGINATION, SET_REQUESTS, SET_REQUESTS_PAGINATION, ADD_REQUESTS, REPLACE_REQUEST_TALENT, REMOVE_REQUEST_TALENT,
  SET_REQUEST_TALENT_PROPERTY, RESET_REQUEST, SET_REQUEST_PROPERTY, REMOVE_ADDITIONAL_TALENT,
  RESET_ADDITIONAL_TALENT, SET_FILTER, RESET_FILTERS, SET_FORM_STATUS
} from 'vx/mutations'

// Request initial state.
import initialRequestState from 'vx/state/booking'
import initialFiltersState from 'vx/state/booking/filters'

// Request modules.
import vetting from './request/vetting'

const modules = {
  vetting
}

// Request state.
const state = {
  request: initialRequestState(),
  filters: merge(initialFiltersState(), {
    order: 'asc'
  }),
  recommendations: [],
  requests: [],
  pagination: {},
  additionalTalent: [],
  formStatus: false
}

// Request state getters.
const getters = {
  /**
   * Initial requests filters state.
   *
   * @return obejct
   */
  initialFiltersState () {
    return merge(initialFiltersState(), {
      order: 'asc'
    })
  },

  /**
   * Determine if we have no more booking requests to load.
   *
   * @return bool
   */
  haveNoMoreBookingRequests: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  },

  /**
   * Determine if we have a booking request.
   *
   * @return bool
   */
  haveBookingRequest: (state) => {
    return state.request !== null && state.request.id !== null
  }
}

// Request actions.
const actions = {
  /**
   * Get a single booking request.
   *
   * @return Promise
   */
  getBookingRequest ({ commit }, { id }) {
    return http.get(`requests/${id}`)
      .then((response) => {
        commit(SET_REQUEST, response.data)

        return response
      })
  },

  getBookingRequestForDuplicate ({ commit }, { id }) {
    return http.get(`requests/${id}`)
      .then((response) => {
        response.data.files = []
        response.data.can_upload_files = 1
        if (response.data.talent) {
          response.data.talent = response.data.talent.map(item => {
            item.response.shortlisted = true
            return item
          })
        }
        commit(SET_REQUEST, response.data)

        return response
      })
  },

  setFormStatus ({ commit }, value) {
    commit(SET_FORM_STATUS, value)
  },

  getFormStatus: (state) => {
    return state.formStatus
  },

  submitBookingRequest ({ commit }, payload) {
    return http.post(`requests`, payload)
      .then((response) => {
        if (response.data.status === 'vetting') {
          // do one thing
        } else {
          // do other thing
        }
        commit(SET_REQUEST, response.data)

        return response
      }).catch(error => {
        if (checkEmail(error)) {
          notify.error('That email has already been taken.')
          return Promise.reject(new Error('That email has already been taken.'))
        }
        return Promise.reject(new Error('Something went wrong. Please check your input and try again.'))
      })
  },

  /**
   * Get a single booking request.
   *
   * @return Promise
   */
  getBookingRecommendations ({ commit }, { id }) {
    return http.get(`recommendations/${id}`)
      .then((response) => {
        commit(SET_RECOMMENDATIONS, response.data)
        commit(SET_RECOMMENDATIONS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Get booking requests.
   *
   * @return Promise
   */
  getBookingRequests ({ commit }, params = {}) {
    return http.get('requests', { params })
      .then((response) => {
        commit(SET_REQUESTS, response.data.data)
        commit(SET_REQUESTS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Get next set of paginated booking requests.
   *
   * @return Promise
   */
  getNextBookingRequests ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreBookingRequests) {
      return Promise.reject(new Error('There are no more booking requests to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`requests`, { params })
      .then((response) => {
        commit(ADD_REQUESTS, response.data.data)
        commit(SET_REQUESTS_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Update a booking requests details.
   *
   * @return Promise
   */
  updateBookingRequest ({ commit }, { id, payload }) {
    return http.put(`requests/${id}`, payload)
      .then(response => {
        commit(SET_REQUEST, response.data)
      })
  },

  /**
   * Update a booking requests details.
   *
   * @return Promise
   */
  updateBookingRequestTags ({ dispatch }, { id, payload }) {
    return http.post(`requests/${id}/tags`, payload)
      .then(response => {
        dispatch('getBookingRequest', {id})
        dispatch('getBookingRecommendations', {id})
        return response
      })
  },

  /**
   * Get number of available talent that have at least one of the booking tags.
   *
   * @param commit
   * @param id
   * @returns Promise
   */
  getTalentTally ({ commit }, { id }) {
    return http.get(`requests/${id}/tags`)
  },

  /**
   * Update a booking requests description tags.
   *
   * @return Promise
   */
  updateBookingRequestDescriptionTags ({ dispatch }, { id, payload }) {
    http.post(`requests/${id}/description_tags`, payload)
      .then(response => {
        dispatch('getBookingRequest', {id})
        dispatch('getBookingRecommendations', {id})
        return response
      })
  },

  /**
   * Close a booking request with less than required stars.
   *
   * @return Promise
   */
  closeBookingRequest ({ commit }, { id }) {
    return http.put(`requests/${id}/close_booking`)
  },

  /**
   * Continue a booking request with less than required stars.
   *
   * @return Promise
   */
  continueBookingRequest ({ commit }, { id }) {
    return http.put(`requests/${id}/continue_booking`)
  },

  respondToBookingRequest ({ commit }, payload) {
    return http.put(`requests/${payload.id}/respond`, payload)
  },

  /**
   * Apply for a booking request as a talent.
   *
   * @return Promise
   */
  acceptBookingRequest ({ commit }, { id, dateAppliedFor, comment }) {
    return http.put(`requests/${id}/accept`, { dateAppliedFor, comment })
  },

  /**
   * Apply for a booking request as a talent.
   *
   * @return Promise
   */
  changeApplicationComment ({ commit }, { id, comment, talentId }) {
    return http.put(`requests/${id}/change_comment/${talentId}`, { comment })
  },

  /**
   * Decline a booking request as a talent.
   *
   * @return Promise
   */
  cancelBookingRequest ({ commit }, { id, reason, comment }) {
    return http.put(`requests/${id}/cancel`, { reason, comment })
  },

  /**
   * Decline a booking request as a talent.
   *
   * @return Promise
   */
  cancelBookingRequestForTalent ({ commit }, { id, reason, comment, talentId }) {
    return http.put(`requests/${id}/cancel/${talentId}`, { reason, comment })
  },

  /**
   * Accept a talent for the booking request.
   *
   * @return Promise
   */
  acceptBookingRequestTalent ({ commit }, { id, talentId }) {
    return http.put(`requests/${id}/accept_talent/${talentId}`)
  },

  /**
   * Unselect a talent for the booking request.
   *
   * @return Promise
   */
  unselectBookingRequestTalent ({ commit }, { id, talentId }) {
    return http.put(`requests/${id}/unselect_talent/${talentId}`)
  },

  /**
   * Reactivate a talent for the booking request.
   *
   * @return Promise
   */
  reactivateTalentApplication ({ commit }, { id, payload }) {
    return http.post(`requests/${id}/reactivate_talent`, payload)
  },

  /**
   * Update a talent for the booking request.
   *
   * @return Promise
   */
  updateBookingRequestTalent ({ commit }, { id, talentId, payload }) {
    return http.put(`requests/${id}/update_talent/${talentId}`, payload)
  },

  acceptBookingRequestAsNonTalent ({ commit }, { id, talentId, payload }) {
    return http.put(`requests/${id}/accept_request/${talentId}`, payload)
  },

  /**
   * Delete a talent from the booking request.
   *
   * @return Promise
   */
  deleteBookingRequestTalent ({ commit }, { id, talentId }) {
    return http.delete(`requests/${id}/remove_talent/${talentId}`)
  },

  /**
   * Withdraw a booking request.
   *
   * @return Promise
   */
  withdrawBookingRequest ({ dispatch }, { id, reason }) {
    return http.delete(`requests/${id}`, { params: { reason } })
      .then((response) => {
        dispatch('setRequestProperty', { property: 'status', value: response.data.status })
        dispatch('setRequestProperty', { property: 'withdraw_reason', value: response.data.withdraw_reason })

        return response
      })
  },

  /**
   * Reactivate a booking request.
   *
   * @return Promise
   */
  reactivateBookingRequest ({ dispatch }, { id }) {
    return http.post(`requests/${id}/reactivate`)
      .then((response) => {
        dispatch('setRequestProperty', { property: 'status', value: response.data.status })
        dispatch('setRequestProperty', { property: 'withdraw_reason', value: null })

        return response
      })
  },

  /**
   * Add additional talent to a booking request.
   *
   * @return Promise
   */
  addAdditionalBookingRequestTalent ({ dispatch }, { id, payload }) {
    return http.put(`requests/${id}/add_additional_talent`, payload)
      .then((response) => {
        dispatch('setRequestProperty', { property: 'talent', value: response.data })

        return response
      })
  },

  /**
   * Send notifications to talent.
   *
   * @return Promise
   */
  sendTalentNotifications ({ dispatch }, { id, payload }) {
    return http.post(`requests/${id}/send_talent_notifications`, payload)
      .then((response) => {
        dispatch('setRequestProperty', { property: 'talent_notified', value: response.data.talent_notified })
        dispatch('setRequestProperty', { property: 'talent_rejected', value: response.data.talent_rejected })
      })
  },

  /**
   * Send notifications to managers.
   *
   * @return Promise
   */
  sendManagerNotifications ({ dispatch }, { id, payload }) {
    return http.post(`requests/${id}/send_manager_notifications`, payload)
      .then((response) => {
        dispatch('setRequestProperty', { property: 'managers_notified', value: response.data })
      })
  },

  /**
   * Remove booking request shortlist talent.
   */
  removeBookingRequestShortlistedTalent ({ commit }, id) {
    commit(REMOVE_REQUEST_TALENT, { id })
  },

  /**
   * Set the booking request.
   */
  setBookingRequest ({ commit }, booking) {
    commit(SET_REQUEST, booking)
  },

  resetBookingRequest ({ commit }) {
    commit(SET_REQUEST, initialRequestState())
  },

  /**
   * Set a request property.
   */
  setRequestProperty ({ commit }, { property, value }) {
    commit(SET_REQUEST_PROPERTY, { property, value })
  },

  /**
   * Set a property on talent for a request based on ID.
   */
  setRequestTalentProperty ({ commit }, { id, property, value }) {
    commit(SET_REQUEST_TALENT_PROPERTY, { id, property, value })
  },

  /**
   * Replace a talent on a request by ID.
   */
  replaceRequestTalent ({ commit }, { id, talent }) {
    commit(REPLACE_REQUEST_TALENT, { id, talent })
  },

  resetRequest ({ commit }) {
    commit(SET_REQUEST, null)
  },

  setFilter ({ commit }, { filter, value }) {
    commit(SET_FILTER, { filter, value })
  },

  resetFilters ({ commit }) {
    commit(RESET_FILTERS)
  }
}

// Request state mutations.
const mutations = {
  [RESET_REQUEST] (state) {
    state.request = null
  },

  [SET_REQUEST] (state, request) {
    state.request = request
  },

  [SET_REQUESTS] (state, requests) {
    state.requests = requests
  },

  [SET_RECOMMENDATIONS] (state, requests) {
    state.recommendations = requests
  },

  [SET_RECOMMENDATIONS_PAGINATION] (state, pagination) {
    state.recommendations_pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [SET_REQUESTS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_REQUESTS] (state, requests) {
    state.requests = concat(state.requests, requests)
  },

  [SET_REQUEST_TALENT_PROPERTY] (state, { id, property, value }) {
    let index = findIndex(state.request.talent, talent => talent.id === id)

    if (index !== -1) {
      set(state.request.talent[index], property, value)
    }
  },

  [REPLACE_REQUEST_TALENT] (state, { id, talent }) {
    let index = findIndex(state.request.talent, talent => talent.id === id)

    state.request.talent.splice(index, 1, talent)
  },

  [REMOVE_REQUEST_TALENT] (state, { id }) {
    let index = findIndex(state.request.talent, talent => talent.id === id)

    state.request.talent.splice(index, 1)
  },

  [SET_REQUEST_PROPERTY] (state, { property, value }) {
    set(state.request, property, value)
  },

  [SET_FORM_STATUS] (state, value) {
    state.formStatus = value
  },

  /**
   * Remove additional talent from the array.
   *
   * @return void
   */
  [REMOVE_ADDITIONAL_TALENT] (state, talent) {
    let index = findIndex(state.additionalTalent, existing => existing.id === talent.id)

    if (index !== -1) {
      state.additionalTalent.splice(index, 1)
    }
  },

  /**
   * Reset the additional talent array.
   *
   * @return void
   */
  [RESET_ADDITIONAL_TALENT] (state) {
    state.additionalTalent = []
  },

  [SET_FILTER] (state, { filter, value }) {
    set(state.filters, filter, value)
  },

  [RESET_FILTERS] (state) {
    state.filters = merge(initialFiltersState(), {
      order: 'asc'
    })
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}
